"use client";

/**
 * Inline-ghost autocomplete hook (B-117, 2026-06-18).
 *
 * Encapsulates:
 *   - 200ms debounce per keystroke
 *   - AbortController on every in-flight request (latest wins)
 *   - IME composition guard (suppresses ghost while CJK input is open)
 *   - Tab / Right-arrow accept semantics
 *   - Esc / Backspace dismissal
 *
 * Consumer pattern:
 *
 *   const { ghost, onKeyDown, onCompositionStart, onCompositionEnd } =
 *     useGhostAutocomplete(value, { enabled: agentEnabled });
 *   // render <input> with these handlers, render <GhostLayer> behind
 *
 * The hook does NOT render anything. It only computes the suffix and
 * exposes key handlers; the visual layer is the caller's
 * responsibility (so each surface — HomeSearchInput, SearchAgainBar —
 * can keep its existing CSS class graph).
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";

import { suggestGhost } from "@/lib/api/suggest";

interface UseGhostAutocompleteOptions {
  /** Master kill-switch. When false the hook is a complete no-op. */
  enabled?: boolean;
  /** Override the default 200ms debounce window. */
  debounceMs?: number;
}

interface UseGhostAutocompleteReturn {
  /** The suffix to render as gray ghost text after `value`. Empty
   * string when no suggestion is active. Never null so consumers
   * can interpolate directly. */
  ghost: string;
  /** Attach to the <input>: handles Tab / Right-arrow accept + Esc dismiss. */
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  /** IME guard. Attach to the <input>. */
  onCompositionStart: () => void;
  onCompositionEnd: () => void;
  /** Set the new value programmatically (used on accept). */
  setValue: (next: string) => void;
}

export function useGhostAutocomplete(
  value: string,
  setExternalValue: (next: string) => void,
  opts: UseGhostAutocompleteOptions = {},
): UseGhostAutocompleteReturn {
  const { enabled = true, debounceMs = 200 } = opts;
  const [canonical, setCanonical] = useState<string | null>(null);
  const dismissedRef = useRef<string>("");
  const abortRef = useRef<AbortController | null>(null);
  const composingRef = useRef(false);

  // Debounced fetch.
  useEffect(() => {
    if (!enabled) {
      setCanonical(null);
      return;
    }
    if (composingRef.current) return;
    const trimmed = (value ?? "").trim();
    if (trimmed.length < 3) {
      setCanonical(null);
      return;
    }
    if (dismissedRef.current && dismissedRef.current === trimmed) {
      setCanonical(null);
      return;
    }
    const t = setTimeout(() => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      suggestGhost(trimmed, "AU", ac.signal).then((c) => {
        if (ac.signal.aborted) return;
        setCanonical(c);
      });
    }, debounceMs);
    return () => clearTimeout(t);
  }, [value, enabled, debounceMs]);

  useEffect(() => () => abortRef.current?.abort(), []);

  // Derive the visible suffix from the canonical phrase.
  const trimmed = (value ?? "").trim();
  let ghost = "";
  if (
    enabled &&
    !composingRef.current &&
    canonical &&
    trimmed.length >= 3 &&
    canonical.toLowerCase().startsWith(trimmed.toLowerCase()) &&
    canonical.length > trimmed.length
  ) {
    // Preserve the canonical's original casing for the suffix.
    ghost = canonical.slice(trimmed.length);
  }

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (!enabled || !ghost || composingRef.current) return;
      const input = e.currentTarget;
      const caretAtEnd = input.selectionStart === input.value.length;
      if (e.key === "Tab" || (e.key === "ArrowRight" && caretAtEnd)) {
        e.preventDefault();
        const accepted = (input.value || "") + ghost;
        setExternalValue(accepted);
        // Optional: emit a telemetry event here (autocomplete.accepted).
      } else if (e.key === "Escape") {
        dismissedRef.current = trimmed;
        setCanonical(null);
      } else if (e.key === "Backspace") {
        // Backspace dismisses the suggestion until the user types
        // something new — feels natural and matches Google/Amazon.
        dismissedRef.current = trimmed;
        setCanonical(null);
      }
    },
    [enabled, ghost, setExternalValue, trimmed],
  );

  const onCompositionStart = useCallback(() => {
    composingRef.current = true;
    setCanonical(null);
  }, []);
  const onCompositionEnd = useCallback(() => {
    composingRef.current = false;
  }, []);

  return {
    ghost,
    onKeyDown,
    onCompositionStart,
    onCompositionEnd,
    setValue: setExternalValue,
  };
}
